import { CloudOutlined, Home, Refresh } from "@mui/icons-material";
import { Box, Breadcrumbs, Button, Card, CardContent, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { COLORS } from "assets";
import { useTranslation } from "hooks";
import PrivateLayout from "layouts/PrivateLayout";
import { useNavigate } from "react-router-dom";

const INTEGRATIONS = [
  {
    title: "REST APIs",
    badge: "API",
    status: "Available",
    subheader: "Create API tokens to get data from Neuravue servers",
    btn: "Manage Tokens",
    href: "/integrations/api-tokens"
  }
];

export default function Integrations() {
  const navigate = useNavigate();
  const t = useTranslation();

  return (
    <PrivateLayout canAccess={(type, role) => !(type === "user" && role === "user")}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Breadcrumbs aria-label="breadcrumb">
          <Tooltip title={t["Home"]}>
            <IconButton
              onClick={() => navigate("/")}
              size="small"
              sx={{
                color: COLORS.surface.secondaryText,
                border: `1px solid ${COLORS.surface.border}`,
                borderRadius: `${COLORS.radius.pill}px`,
                bgcolor: COLORS.surface.card,
                width: 32,
                height: 32
              }}
            >
              <Home sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Typography color="text.primary" sx={{ fontWeight: 500 }}>
            {t["Integrations"]}
          </Typography>
        </Breadcrumbs>
        <Tooltip title={t["Refresh"]}>
          <IconButton
            sx={{
              bgcolor: COLORS.surface.card,
              color: COLORS.surface.primaryText,
              border: `1px solid ${COLORS.surface.border}`,
              borderRadius: `${COLORS.radius.pill}px`,
              width: 40,
              height: 40,
              "&:hover": { bgcolor: COLORS.surface.tableHeader }
            }}
          >
            <Refresh sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box mb={4}>
        <Typography
          variant="h1"
          sx={{ color: COLORS.surface.primaryText, fontSize: { xs: 24, md: 28 }, fontWeight: 600, mb: 0.5 }}
        >
          {t["3rd Party Integrations"]}
        </Typography>
        <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 15, maxWidth: 560 }}>
          {t["Allow external APIs to interact with Neuravue Detection platform and access your data."]}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 2.5
        }}
      >
        {INTEGRATIONS.map((item) => (
          <Card
            key={item.href}
            elevation={0}
            sx={{
              bgcolor: COLORS.surface.card,
              borderRadius: `${COLORS.radius.lg}px`,
              border: `1px solid ${COLORS.surface.border}`,
              boxShadow: COLORS.shadow.card,
              "&:hover": { borderColor: COLORS.primary }
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 56,
                    height: 56,
                    borderRadius: `${COLORS.radius.md}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${COLORS.custom.prelude}, ${COLORS.custom.lightblue})`
                  }}
                >
                  <CloudOutlined sx={{ fontSize: 28, color: COLORS.brand.purple }} />
                </Box>

                <Box flex={1} minWidth={0}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 16 }}>
                        {t[item.title]}
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: COLORS.brand.purple,
                          color: COLORS.white,
                          fontSize: 11,
                          fontWeight: 600,
                          lineHeight: 1,
                          px: 1,
                          py: 0.5,
                          borderRadius: `${COLORS.radius.pill}px`
                        }}
                      >
                        {t[item.badge]}
                      </Box>
                    </Stack>
                    <Box
                      sx={{
                        bgcolor: COLORS.success + "1A",
                        color: COLORS.success,
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: `${COLORS.radius.pill}px`,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {t[item.status]}
                    </Box>
                  </Stack>

                  <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 13, mt: 1, lineHeight: 1.5 }}>
                    {t[item.subheader]}
                  </Typography>

                  <Button
                    onClick={() => navigate(item.href)}
                    variant="contained"
                    color="primary"
                    sx={{
                      mt: 2.5,
                      textTransform: "none",
                      fontWeight: 500,
                      borderRadius: `${COLORS.radius.md}px`,
                      color: COLORS.white,
                      backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
                      boxShadow: "none",
                      "&:hover": {
                        backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
                        filter: "brightness(1.05)",
                        boxShadow: "none"
                      }
                    }}
                  >
                    {t[item.btn]}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>
    </PrivateLayout>
  );
}
